import React, { useState } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, Filter } from "lucide-react"
import { useProjects } from "../../hooks/useProjects"

const categories = ["All", "Architecture", "Interior Design", "3D Visualization", "Urban Planning"]

export default function Works() {
  const [activeCategory, setActiveCategory] = useState("All")
  const { projects, loading, error } = useProjects()

  const filtered =
    activeCategory === "All"
      ? projects
      : projects.filter((p) => p.category === activeCategory)

  return (
    <main className="pt-24 min-h-screen bg-stone-950">
      {/* Page Header */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
          <span className="w-6 h-px bg-amber-400" />
          Portfolio
        </span>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <h1 className="font-['Cormorant_Garamond'] text-5xl lg:text-7xl font-light text-stone-100 leading-tight">
            Our Works
          </h1>
          <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed max-w-sm">
            A selection of projects across architecture, interior design, visualization, and urban planning — each one a distinct response to a specific brief.
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 mb-12">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={13} className="text-stone-600 mr-1" />
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`font-['Space_Grotesk'] text-[10px] tracking-[0.2em] px-4 py-2 border transition-all duration-200 ${
                activeCategory === cat
                  ? "bg-amber-400 border-amber-400 text-stone-950"
                  : "border-stone-700 text-stone-500 hover:border-stone-500 hover:text-stone-300"
              }`}
            >
              {cat.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 pb-24">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-72 lg:h-80 bg-stone-800 mb-5" />
                <div className="h-3 bg-stone-800 w-1/3 mb-3" />
                <div className="h-5 bg-stone-800 w-2/3 mb-2" />
                <div className="h-3 bg-stone-800 w-1/2" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-24">
            <p className="font-['Inter'] text-sm text-red-400">Failed to load projects. Please refresh.</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((project) => (
                <Link
                  key={project.id}
                  to={`/works/${project.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden h-72 lg:h-80 mb-5">
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {project.featured && (
                      <span className="absolute top-4 left-4 font-['Space_Grotesk'] text-[9px] tracking-[0.2em] bg-amber-400 text-stone-950 px-3 py-1 uppercase">
                        Featured
                      </span>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-stone-950/0 group-hover:bg-stone-950/30 transition-colors duration-400" />
                    {/* Arrow */}
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-10 h-10 bg-amber-400 flex items-center justify-center">
                        <ArrowRight size={16} className="text-stone-950" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-['Space_Grotesk'] text-[9px] tracking-[0.3em] text-amber-400 uppercase">
                        {project.category}
                      </span>
                      <span className="font-['Space_Grotesk'] text-[9px] tracking-widest text-stone-600">
                        {project.year}
                      </span>
                    </div>
                    <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-stone-100 mb-1 group-hover:text-amber-200 transition-colors">
                      {project.title}
                    </h3>
                    <p className="font-['Inter'] text-xs text-stone-500 mb-4">{project.location}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(project.tags || []).map((tag) => (
                        <span
                          key={tag}
                          className="font-['Space_Grotesk'] text-[9px] tracking-[0.15em] text-stone-600 border border-stone-800 px-2.5 py-1 uppercase"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-24">
                <p className="font-['Cormorant_Garamond'] text-3xl text-stone-600">No projects found</p>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  )
}
